import { useState } from 'react';

import { useAppDispatch } from '@verantos-dev/core-annotation-tool';

import {
  removeAnnotationAgentSection,
  saveAnnotationAgentSection,
  type AgentSection,
} from '../../state/createProjectSlice';
import {
  validateAgentSectionDraft,
  type AgentSectionErrors,
} from '../../utils/agentSectionValidation';

// TODO: cleanup with cp_annotation_createproject_agent ff

export interface AgentSectionDraft {
  heading: string;
  body: string;
}

interface UseAgentSectionCardProps {
  id: string;
  /** The saved section, or undefined for a section that has never been saved. */
  section: AgentSection | undefined;
  /** Called when a never-saved section is saved, cancelled or removed, so its card goes away. */
  onNewSectionResolved: (id: string) => void;
}

interface UseAgentSectionCardResult {
  isExpanded: boolean;
  draft: AgentSectionDraft | null;
  headerLabel: string | null;
  showHeadingError: boolean;
  showBodyError: boolean;
  handleToggleExpanded: () => void;
  handleDraftChange: (changes: Partial<AgentSectionDraft>) => void;
  handleSave: () => void;
  handleCancel: () => void;
  handleRemove: () => void;
}

/**
 * Local UI state of one custom section card. Only a valid Save writes to Redux, so a draft
 * or a never-saved section never reaches the prompt.
 */
export const useAgentSectionCard = ({
  id,
  section,
  onNewSectionResolved,
}: UseAgentSectionCardProps): UseAgentSectionCardResult => {
  const dispatch = useAppDispatch();
  const isNew = section === undefined;
  const [isExpanded, setIsExpanded] = useState(isNew);
  const [draft, setDraft] = useState<AgentSectionDraft | null>(
    isNew ? { heading: '', body: '' } : null
  );
  const [hasTriedSave, setHasTriedSave] = useState(false);

  const errors: AgentSectionErrors = draft
    ? validateAgentSectionDraft(draft)
    : { heading: false, body: false };

  // A new section shows what is being typed; a saved one shows its saved heading.
  const headerLabel = (isNew ? draft?.heading : section.heading) || null;

  const discardDraft = () => {
    if (isNew) {
      onNewSectionResolved(id);
      return;
    }
    setDraft(null);
    setIsExpanded(false);
    setHasTriedSave(false);
  };

  // Collapsing an open card discards its draft, as Cancel does.
  const handleToggleExpanded = () => {
    if (isExpanded) {
      discardDraft();
      return;
    }
    setDraft({ heading: section?.heading ?? '', body: section?.body ?? '' });
    setHasTriedSave(false);
    setIsExpanded(true);
  };

  const handleDraftChange = (changes: Partial<AgentSectionDraft>) => {
    setDraft((current) => (current ? { ...current, ...changes } : current));
  };

  const handleSave = () => {
    if (!draft) return;
    if (errors.heading || errors.body) {
      setHasTriedSave(true);
      return;
    }
    dispatch(saveAnnotationAgentSection({ id, heading: draft.heading.trim(), body: draft.body }));
    if (isNew) {
      onNewSectionResolved(id);
      return;
    }
    setDraft(null);
    setIsExpanded(false);
    setHasTriedSave(false);
  };

  const handleRemove = () => {
    if (isNew) {
      onNewSectionResolved(id);
      return;
    }
    dispatch(removeAnnotationAgentSection(id));
  };

  return {
    isExpanded,
    draft,
    headerLabel,
    showHeadingError: hasTriedSave && errors.heading,
    showBodyError: hasTriedSave && errors.body,
    handleToggleExpanded,
    handleDraftChange,
    handleSave,
    handleCancel: discardDraft,
    handleRemove,
  };
};
